import { format } from "date-fns";

export type AuditActor = {
  id: string;
  name: string;
  email: string;
} | null;

export type RawActivityLog = {
  id: string;
  company_id: string;
  user_id: string | null;
  action_type: string;
  resource_type: string;
  resource_id: string | null;
  resource_title: string | null;
  details: unknown;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  user?: AuditActor;
};

export type AuditFieldChange = {
  field: string;
  from: string;
  to: string;
};

export type AuditEvent = {
  id: string;
  timestamp: string;
  actorName: string;
  actorEmail: string | null;
  actorType: "User" | "System";
  module: string;
  eventName: string;
  eventCategory: string;
  outcome: "Success" | "Warning" | "Failure" | "Info";
  targetType: string;
  targetName: string;
  targetId: string | null;
  summary: string;
  statusChange: string | null;
  fieldChanges: AuditFieldChange[];
  source: string;
  ipAddress: string | null;
  device: string | null;
  raw: RawActivityLog;
};

type AuditActionDefinition = {
  label: string;
  category: string;
  outcome?: AuditEvent["outcome"];
  module?: string;
};

const RESOURCE_MODULES: Record<string, string> = {
  policy: "Policy",
  policy_template: "Policy",
  template_stack: "Policy",
  workflow: "Policy",
  user: "Team & Admin",
  team: "Team & Admin",
  company: "Team & Admin",
  billing: "Billing",
  subscription: "Billing",
  governance: "Governance",
  committee: "Governance",
  tor: "Governance",
  vote: "Governance",
  compliance_task: "Compliance",
  compliance: "Compliance",
  risk: "Risk",
  risk_register: "Risk",
  knowledge_base: "Knowledge Base",
  knowledge_document: "Knowledge Base",
  ai_agent: "AI Agent",
  quiz: "Quizzes",
};

const ACTIONS: Record<string, AuditActionDefinition> = {
  policy_created: { label: "Policy Created", category: "Create", outcome: "Success", module: "Policy" },
  policy_edited: { label: "Policy Edited", category: "Update", outcome: "Success", module: "Policy" },
  policy_sent_to_draft: { label: "Policy Returned to Draft", category: "Workflow", outcome: "Warning", module: "Policy" },
  policy_reviewed: { label: "Policy Reviewed", category: "Review", outcome: "Success", module: "Policy" },
  policy_approved: { label: "Policy Approved", category: "Approval", outcome: "Success", module: "Policy" },
  policy_rejected: { label: "Policy Rejected", category: "Approval", outcome: "Failure", module: "Policy" },
  policy_deleted: { label: "Policy Deleted", category: "Delete", outcome: "Warning", module: "Policy" },
  policy_translated: { label: "Policy Translated", category: "Localization", outcome: "Success", module: "Policy" },
  policy_created_from_template: { label: "Policy Created from Template", category: "Create", outcome: "Success", module: "Policy" },
  policy_created_from_stack: { label: "Policy Created from Stack", category: "Create", outcome: "Success", module: "Policy" },
  template_created_from_policy: { label: "Template Created from Policy", category: "Create", outcome: "Success", module: "Policy" },
  workflow_status_changed: { label: "Workflow Status Changed", category: "Workflow", outcome: "Info", module: "Policy" },
  user_invited: { label: "User Invited", category: "Access", outcome: "Success", module: "Team & Admin" },
  user_joined: { label: "User Joined", category: "Access", outcome: "Success", module: "Team & Admin" },
};

const STATUS_KEYS = [
  ["from_status", "to_status"],
  ["previous_status", "new_status"],
  ["old_status", "new_status"],
];

const RESERVED_DETAIL_KEYS = new Set([
  "action",
  "changes",
  "from_status",
  "to_status",
  "previous_status",
  "new_status",
  "old_status",
  "resource_id",
  "resource_title",
]);

function humanize(value: string) {
  return value
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "string") return humanize(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function getDetailsObject(details: unknown): Record<string, unknown> {
  if (!details || typeof details !== "object" || Array.isArray(details)) return {};
  return details as Record<string, unknown>;
}

function getStatusChange(details: Record<string, unknown>) {
  for (const [fromKey, toKey] of STATUS_KEYS) {
    const fromValue = details[fromKey];
    const toValue = details[toKey];
    if (fromValue || toValue) {
      return `${stringifyValue(fromValue)} -> ${stringifyValue(toValue)}`;
    }
  }

  return null;
}

function getFieldChanges(details: Record<string, unknown>): AuditFieldChange[] {
  const changes = details.changes;

  if (changes && typeof changes === "object" && !Array.isArray(changes)) {
    return Object.entries(changes as Record<string, unknown>).map(([field, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const candidate = value as Record<string, unknown>;
        if ("from" in candidate || "to" in candidate || "old" in candidate || "new" in candidate) {
          return {
            field: humanize(field),
            from: stringifyValue(candidate.from ?? candidate.old),
            to: stringifyValue(candidate.to ?? candidate.new),
          };
        }
      }

      return {
        field: humanize(field),
        from: "N/A",
        to: stringifyValue(value),
      };
    });
  }

  const statusChange = getStatusChange(details);
  if (statusChange) {
    const [from, to] = statusChange.split(" -> ");
    return [{ field: "Status", from, to }];
  }

  return Object.entries(details)
    .filter(([key]) => !RESERVED_DETAIL_KEYS.has(key))
    .slice(0, 4)
    .map(([key, value]) => ({
      field: humanize(key),
      from: "N/A",
      to: stringifyValue(value),
    }));
}

function getDeviceSummary(userAgent: string | null) {
  if (!userAgent) return null;

  const browser = userAgent.includes("Edg/")
    ? "Edge"
    : userAgent.includes("Chrome/")
      ? "Chrome"
      : userAgent.includes("Safari/")
        ? "Safari"
        : userAgent.includes("Firefox/")
          ? "Firefox"
          : "Browser";

  const os = userAgent.includes("Mac OS X")
    ? "macOS"
    : userAgent.includes("Windows")
      ? "Windows"
      : userAgent.includes("Android")
        ? "Android"
        : userAgent.includes("iPhone") || userAgent.includes("iPad")
          ? "iOS"
          : "Device";

  return `${browser} on ${os}`;
}

function getSummary(log: RawActivityLog, eventName: string, details: Record<string, unknown>, statusChange: string | null) {
  if (typeof details.action === "string") {
    return details.action;
  }

  if (statusChange) {
    return `${eventName}: ${statusChange}`;
  }

  const target = log.resource_title || humanize(log.resource_type);
  return `${eventName} for ${target}`;
}

export function toAuditEvent(log: RawActivityLog): AuditEvent {
  const details = getDetailsObject(log.details);
  const definition = ACTIONS[log.action_type];
  const module = definition?.module || RESOURCE_MODULES[log.resource_type] || humanize(log.resource_type);
  const eventName = definition?.label || humanize(log.action_type);
  const statusChange = getStatusChange(details);
  const actorName = log.user?.name || "System";

  return {
    id: log.id,
    timestamp: log.created_at,
    actorName,
    actorEmail: log.user?.email || null,
    actorType: log.user ? "User" : "System",
    module,
    eventName,
    eventCategory: definition?.category || "Activity",
    outcome: definition?.outcome || "Info",
    targetType: humanize(log.resource_type),
    targetName: log.resource_title || "Untitled resource",
    targetId: log.resource_id,
    summary: getSummary(log, eventName, details, statusChange),
    statusChange,
    fieldChanges: getFieldChanges(details),
    source: log.user_id ? "Web App" : "System",
    ipAddress: log.ip_address,
    device: getDeviceSummary(log.user_agent),
    raw: log,
  };
}

export function formatAuditDate(timestamp: string) {
  return format(new Date(timestamp), "MMM d, yyyy");
}

export function formatAuditTime(timestamp: string) {
  return format(new Date(timestamp), "HH:mm");
}

export function formatAuditTimestamp(timestamp: string) {
  return format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss");
}

export function uniqueSorted(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

